import { Injectable } from '@angular/core';
import { Firestore, CollectionReference, DocumentData, collection, query, where, orderBy, getDocs, onSnapshot, QuerySnapshot } from '@angular/fire/firestore';
import { Observable, BehaviorSubject, from, throwError } from 'rxjs';
import { map, catchError, finalize } from 'rxjs/operators';

// Interface for the record within the new document structure
interface GridPowerRecord {
  time: string;
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
  value: 0 | 1;
  timestamp: number;
}

// Interface for the new document structure in Firestore
interface GridPowerDocument {
  date: string;
  records: GridPowerRecord[];
  timestamp: number;
}

// Define the interface for inverter stats exactly as provided - keeping this for backward compatibility
export interface InverterStat {
  time: string;
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
  value: 0 | 1;
  timestamp: number;
}

// Define ECharts data format
export interface EChartsInverterData {
  xAxis: string[];  // Time values
  series: number[]; // Value data points (0 or 1)
  timestamps: number[]; // Original timestamps
}

@Injectable({
  providedIn: 'root'
})
export class InverterStatsService {
  private inverterStatsCollection: CollectionReference<DocumentData>;
  
  // BehaviorSubjects to track loading state and any errors
  private loadingSubject = new BehaviorSubject<boolean>(false);
  private errorSubject = new BehaviorSubject<string | null>(null);
  
  // Observable streams that components can subscribe to
  public loading$ = this.loadingSubject.asObservable();
  public error$ = this.errorSubject.asObservable();

  constructor(private firestore: Firestore) {
    this.inverterStatsCollection = collection(this.firestore, 'Inverter/UvpalmWrH9R8RFWglBfq/GridPower');
  }

  /**
   * Fetch inverter stats within a specified date range
   * @param startTimestamp - Start timestamp in milliseconds
   * @param endTimestamp - End timestamp in milliseconds
   * @returns Observable of InverterStat array
   */
  public getInverterStatsByDateRange(startTimestamp: number, endTimestamp: number): Observable<InverterStat[]> {
    this.loadingSubject.next(true);
    this.errorSubject.next(null);

    try {
      // Create a query to filter documents by day-level timestamp
      // Use provided timestamps directly to maintain timezone consistency
      const startOfDay = new Date(startTimestamp);
      startOfDay.setHours(0, 0, 0, 0);
      const endOfDay = new Date(endTimestamp);
      endOfDay.setHours(23, 59, 59, 999);
      
      const statsQuery = query(
        this.inverterStatsCollection,
        where('timestamp', '>=', startOfDay.getTime()),
        where('timestamp', '<=', endOfDay.getTime()),
        orderBy('timestamp', 'asc')
      );

      // Execute the query and convert result to Observable
      return from(getDocs(statsQuery)).pipe(
        map(this.convertQuerySnapshotToStats),
        catchError(error => {
          console.error('Error fetching inverter stats:', error);
          this.errorSubject.next('Failed to fetch inverter stats data');
          return throwError(() => new Error('Failed to fetch inverter stats data'));
        }),
        finalize(() => this.loadingSubject.next(false))
      );
    } catch (error) {
      this.loadingSubject.next(false);
      this.errorSubject.next('Error creating query for inverter stats');
      return throwError(() => new Error('Failed to create query for inverter stats'));
    }
  }

  /**
   * Set up a real-time listener for inverter stats within a date range
   * @param startTimestamp - Start timestamp in milliseconds
   * @param endTimestamp - End timestamp in milliseconds
   * @returns Observable of InverterStat array that updates in real-time
   */
  public getRealtimeInverterStats(startTimestamp: number, endTimestamp: number): Observable<InverterStat[]> {
    const statsSubject = new BehaviorSubject<InverterStat[]>([]);
    this.loadingSubject.next(true);
    this.errorSubject.next(null);

    try {
      // Create a query to filter documents by day-level timestamp
      // Use provided timestamps directly to maintain timezone consistency
      const startOfDay = new Date(startTimestamp);
      startOfDay.setHours(0, 0, 0, 0);
      const endOfDay = new Date(endTimestamp);
      endOfDay.setHours(23, 59, 59, 999);
      
      const statsQuery = query(
        this.inverterStatsCollection,
        where('timestamp', '>=', startOfDay.getTime()),
        where('timestamp', '<=', endOfDay.getTime()),
        orderBy('timestamp', 'asc')
      );

      // Set up the real-time listener
      const unsubscribe = onSnapshot(
        statsQuery,
        (snapshot) => {
          const stats = this.convertQuerySnapshotToStats(snapshot);
          statsSubject.next(stats);
          this.loadingSubject.next(false);
        },
        (error) => {
          console.error('Error in real-time listener:', error);
          this.errorSubject.next('Real-time data stream error');
          this.loadingSubject.next(false);
          statsSubject.error(error);
        }
      );

      // Return the Observable and handle cleanup when it's unsubscribed
      return statsSubject.asObservable().pipe(
        finalize(() => {
          unsubscribe();
          this.loadingSubject.next(false);
        })
      );
    } catch (error) {
      this.loadingSubject.next(false);
      this.errorSubject.next('Error setting up real-time data stream');
      return throwError(() => new Error('Failed to set up real-time data stream'));
    }
  }

  /**
   * Transform inverter stats data for ECharts
   * @param stats - Array of InverterStat objects
   * @returns Formatted data for ECharts
   */
  public transformForECharts(stats: InverterStat[]): EChartsInverterData {
    const xAxis: string[] = [];
    const series: number[] = [];
    const timestamps: number[] = [];

    stats.forEach(stat => {
      xAxis.push(stat.time);
      series.push(stat.value);
      timestamps.push(stat.timestamp);
    });

    return { xAxis, series, timestamps };
  }

  /**
   * Get time-aggregated data for different zoom levels
   * @param stats - Array of InverterStat objects
   * @param aggregation - 'minute', 'hour', 'day'
   * @returns Aggregated data for ECharts
   */
  public getAggregatedData(stats: InverterStat[], aggregation: 'minute' | 'hour' | 'day'): EChartsInverterData {
    const aggregatedMap = new Map<string, { count: number, sum: number, timestamp: number }>();
    
    stats.forEach(stat => {
      let key: string;
      
      // Add 1 to month since it's 0-based in the data
      const adjustedMonth = (stat.month + 1).toString().padStart(2, '0');
      const adjustedDay = stat.day.toString().padStart(2, '0');
      const adjustedHour = stat.hour.toString().padStart(2, '0');
      const adjustedMinute = stat.minute.toString().padStart(2, '0');

      if (aggregation === 'minute') {
        key = `${stat.year}-${adjustedMonth}-${adjustedDay} ${adjustedHour}:${adjustedMinute}`;
      } else if (aggregation === 'hour') {
        key = `${stat.year}-${adjustedMonth}-${adjustedDay} ${adjustedHour}:00`;
      } else { // day
        key = `${stat.year}-${adjustedMonth}-${adjustedDay}`;
      }
      
      if (!aggregatedMap.has(key)) {
        aggregatedMap.set(key, { count: 0, sum: 0, timestamp: stat.timestamp });
      }
      
      const current = aggregatedMap.get(key)!;
      current.count += 1;
      current.sum += stat.value;
      // Keep the earliest timestamp for each aggregated period
      current.timestamp = Math.min(current.timestamp, stat.timestamp);
    });
    
    const xAxis: string[] = [];
    const series: number[] = [];
    const timestamps: number[] = [];
    
    // Convert the map to arrays, sorted by timestamp
    Array.from(aggregatedMap.entries())
      .sort((a, b) => a[1].timestamp - b[1].timestamp)
      .forEach(([key, data]) => {
        xAxis.push(key);
        // Calculate average value rounded to 0 or 1
        series.push(data.sum / data.count >= 0.5 ? 1 : 0);
        timestamps.push(data.timestamp);
      });
    
    return { xAxis, series, timestamps };
  }

  /**
   * Helper method to convert a Firestore QuerySnapshot to an array of InverterStat objects
   * @param snapshot - Firestore QuerySnapshot
   * @returns Array of InverterStat objects
   */
  private convertQuerySnapshotToStats(snapshot: QuerySnapshot<DocumentData>): InverterStat[] {
    const allRecords: InverterStat[] = [];
    
    snapshot.docs.forEach(doc => {
      const data = doc.data() as GridPowerDocument;
      // Add all records from this document if they exist
      if (data.records && Array.isArray(data.records)) {
        allRecords.push(...data.records);
      }
    });
    
    // Sort by timestamp to maintain chronological order
    return allRecords.sort((a, b) => a.timestamp - b.timestamp);
  }
}
